/**
 * Shuffle a string using Fisher-Yates (Knuth) algorithm
 * @param {string} str - The string to shuffle
 * @returns {string} - Shuffled string
 */
export const shuffleString = (str) => {
  if (!str || typeof str !== 'string') return '';
  
  // Convert string to array for shuffling
  const arr = str.split('');
  
  // Fisher-Yates (Knuth) shuffle algorithm
  for (let i = arr.length - 1; i > 0; i--) {
    // Generate random index between 0 and i
    const j = Math.floor(Math.random() * (i + 1));
    
    // Swap elements at positions i and j
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  
  return arr.join('');
};

/**
 * Alternative: Shuffle string using sort with random comparison
 * Note: This is less efficient and not truly random, but simple
 * @param {string} str - The string to shuffle
 * @returns {string} - Shuffled string
 */
export const shuffleStringSimple = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.split('').sort(() => Math.random() - 0.5).join('');
};

/**
 * Shuffle string with seed for predictable results
 * @param {string} str - The string to shuffle
 * @param {number} seed - Seed for random number generator
 * @returns {string} - Shuffled string
 */
export const shuffleStringWithSeed = (str, seed = Date.now()) => {
  if (!str || typeof str !== 'string') return '';
  
  const arr = str.split('');
  
  // Seeded random number generator
  const seededRandom = (seed) => {
    let value = seed;
    return () => {
      value = (value * 9301 + 49297) % 233280;
      return value / 233280;
    };
  };
  
  const random = seededRandom(seed);
  
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  
  return arr.join('');
};

/**
 * Generate a unique ID from user IDs for call ID
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {string} - Shuffled combined ID
 */
export const generateCallId = (userId1, userId2) => {
  if (!userId1 || !userId2) return '';
  
  // Combine user IDs and shuffle
  const combined = `${userId1}-${userId2}`;
  return shuffleString(combined);
};

/**
 * Generate a secure random string
 * @param {number} length - Length of the random string
 * @returns {string} - Random string
 */
export const generateRandomString = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  if (window.crypto && crypto.getRandomValues) {
    // Use cryptographically secure random numbers
    const values = new Uint32Array(length);
    crypto.getRandomValues(values);
    
    for (let i = 0; i < length; i++) {
      result += chars[values[i] % chars.length];
    }
  } else {
    // Fallback to Math.random (less secure)
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
  }
  
  return result;
};

/**
 * Shuffle string while preserving certain positions
 * @param {string} str - The string to shuffle
 * @param {number[]} fixedPositions - Positions to keep fixed (0-indexed)
 * @returns {string} - Shuffled string with fixed positions
 */
export const shuffleStringWithFixedPositions = (str, fixedPositions = []) => {
  if (!str || typeof str !== 'string') return '';
  
  const arr = str.split('');
  const fixedChars = new Map();
  
  // Store fixed characters and replace with placeholder
  fixedPositions.sort((a, b) => a - b).forEach(pos => {
    if (pos >= 0 && pos < arr.length) {
      fixedChars.set(pos, arr[pos]);
      arr[pos] = null; // Placeholder
    }
  });
  
  // Shuffle only the non-fixed positions
  const shufflableIndices = arr.map((char, index) => char === null ? null : index)
    .filter(index => index !== null);
  
  for (let i = shufflableIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = arr[shufflableIndices[i]];
    arr[shufflableIndices[i]] = arr[shufflableIndices[j]];
    arr[shufflableIndices[j]] = temp;
  }
  
  // Restore fixed characters
  fixedChars.forEach((char, pos) => {
    arr[pos] = char;
  });
  
  return arr.join('');
};

/**
 * Test the randomness of shuffle function
 * @param {function} shuffleFn - Shuffle function to test
 * @param {string} testString - String to test with
 * @param {number} iterations - Number of iterations
 * @returns {Object} - Test results
 */
export const testShuffleRandomness = (shuffleFn = shuffleString, testString = 'abcde', iterations = 10000) => {
  const results = {};
  
  for (let i = 0; i < iterations; i++) {
    const shuffled = shuffleFn(testString);
    results[shuffled] = (results[shuffled] || 0) + 1;
  }
  
  // Calculate distribution
  const uniqueResults = Object.keys(results).length;
  const expectedUnique = factorial(testString.length);
  const coverage = (uniqueResults / Math.min(iterations, expectedUnique)) * 100;
  
  return {
    totalIterations: iterations,
    uniqueResults,
    expectedUniquePermutations: expectedUnique,
    coveragePercentage: coverage.toFixed(2),
    distribution: Object.entries(results)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10) // Top 10 most common results
  };
};

/**
 * Helper function to calculate factorial
 */
const factorial = (n) => {
  if (n <= 1) return 1;
  return n * factorial(n - 1);
};

/**
 * Create a call ID by combining and shuffling user IDs
 * This ensures unique call IDs for each user pair
 * @param {string} userId - Current user ID
 * @param {string} otherUserId - Other user ID
 * @returns {string} - Unique call ID
 */
export const createCallId = (userId, otherUserId) => {
  if (!userId || !otherUserId) {
    throw new Error('Both user IDs are required to create call ID');
  }
  
  // Sort IDs to ensure same combination always produces same base
  const sortedIds = [userId, otherUserId].sort();
  
  // Create a deterministic hash from the combination
  const baseString = sortedIds.join('|');
  
  // Add timestamp for uniqueness
  const timestamp = Date.now().toString(36);
  
  // Combine and shuffle
  const combined = baseString + timestamp;
  
  // Use secure shuffle with seed for consistency
  return shuffleStringWithSeed(combined, hashCode(baseString));
};

/**
 * Simple string hash function
 * @param {string} str - String to hash
 * @returns {number} - Hash code
 */
const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Generate a unique room ID for chat/call
 * @param {string} type - Type of room ('call', 'chat', 'group')
 * @param {...string} participants - Participant IDs
 * @returns {string} - Unique room ID
 */
export const generateRoomId = (type = 'call', ...participants) => {
  const sortedParticipants = participants.sort();
  const participantsHash = sortedParticipants.join('-');
  const timestamp = Date.now().toString(36);
  const random = generateRandomString(4);
  
  const baseId = `${type}-${participantsHash}-${timestamp}-${random}`;
  
  // Shuffle to make it less predictable
  return shuffleString(baseId);
};


export const capitalizeFirstLetter = (string = "") => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const createSlugFromText = (string = "") => {
  return string
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric characters with hyphens
    .replace(/^-+|-+$/g, '');    // Remove leading and trailing hyphens
}

export const formatNumber = (number) => {
  if (number < 1000) {
    return number.toString();
  } else if (number < 1000000) {
    return (number / 1000).toFixed(1) + 'K';
  } else {
    return (number / 1000000).toFixed(1) + 'M';
  }
};

// Default export
export default {
  shuffleString,
  shuffleStringSimple,
  shuffleStringWithSeed,
  shuffleStringWithFixedPositions,
  generateRandomString,
  generateCallId,
  createCallId,
  generateRoomId,
  testShuffleRandomness,
  capitalizeFirstLetter
};